import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { TrustBar } from '@/components/TrustBadges';
import { ShieldCheck, CalendarBlank, Clock, MapPin, ArrowLeft, Lock, ChatCircle, Star, ArrowsClockwise, Scales, Lightning, Fire, Users, CheckCircle, Eye } from '@phosphor-icons/react';
import { CANCELLATION_POLICIES, describePolicy } from '@/lib/policies';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DURATIONS = [
  { label: '2 hours', hours: 2 },
  { label: '4 hours', hours: 4 },
  { label: '6 hours', hours: 6 },
  { label: 'Full night (8h)', hours: 8 },
];
const CAT_EMOJI = { 'DJs':'🎧','Photographers':'📸','Cleaners':'🧹','Private Chefs':'🍽️','Videographers':'🎥','Bartenders':'🍹','Decorators':'🎨','Tutors':'📚','Plumbers':'🔧','Electricians':'⚡','Gardeners':'🌿','Movers':'📦','Pet Sitters':'🐶','Barbers':'💇','Makeup Artists':'💆','Caterers':'🍽️','Venues':'🏛️','Event Decor':'🎨','Event Planners':'📋','Live Bands':'🎸','Performers':'🎤','Rentals':'🪑','Servers':'🍽️','Security':'🛡️','Catering':'🍽️' };

export default function CheckoutPage() {
  const { profileId, serviceId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingDate, setBookingDate] = useState(searchParams.get('date') || '');
  const [startTime, setStartTime] = useState(searchParams.get('time') || '');
  const [duration, setDuration] = useState(4);
  const [guests, setGuests] = useState(1);
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/providers/${profileId}`);
        const svc = res.data.services?.find(s => s.service_id === serviceId);
        // Quote-only services have no fixed price — you can't check out. Send the
        // customer to the profile's Request-a-Quote flow instead.
        if (svc?.price_type === 'quote') {
          navigate(`/provider/${profileId}`, { replace: true });
          return;
        }
        setProvider(res.data);
        setService(svc);
      } catch (err) { console.error("Checkout data error:", err); } finally { setLoading(false); }
    };
    fetchData();
  }, [profileId, serviceId, navigate]);

  // Set default date on mount only
  useEffect(() => {
    if (!searchParams.get('date')) {
      const d = new Date(); d.setDate(d.getDate() + 7);
      setBookingDate(d.toISOString().split('T')[0]);
    }
  }, [searchParams]);

  // Mirrors backend compute_service_subtotal exactly: hourly = base × hours,
  // per-person = base × guests, flat = base. A range books at its low end
  // (base_price was stored as price_min), then the vendor can adjust via quote.
  const isHourly = service?.price_type === 'hourly';
  const isPerPerson = service?.price_type === 'per_person';
  const isRange = service?.price_type === 'range';
  const isEvent = !isHourly;
  const unit = (service?.base_price
    ?? (isRange ? service?.price_min : undefined)
    ?? (isHourly ? provider?.hourly_rate : undefined)) || 0;
  const base = isHourly
    ? Math.round(unit * duration * 100) / 100
    : isPerPerson
      ? Math.round(unit * Math.max(1, guests) * 100) / 100
      : isRange
        ? (service?.price_min || service?.base_price || 0)
        : (service?.base_price || 0);
  // Customer pays the listed price — Bookr's 6% comes out of the provider's payout, not added on top.
  const total = base;
  // Prices are charged in CAD — label it so no one mistakes it for USD.
  const money = (v) => `$${v.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CAD`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      // 1. Create the booking
      const bookRes = await axios.post(`${API}/bookings`, {
        provider_id: provider.user_id, service_id: serviceId,
        booking_date: bookingDate, start_time: startTime, end_time: '',
        location_address: address,
        notes: `${isHourly ? `Duration: ${duration}h. ` : isPerPerson ? `Guests: ${guests}. ` : ''}${notes}`,
        hours: duration,
        guests: isPerPerson ? Math.max(1, guests) : undefined,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const bookingId = bookRes.data.booking_id;

      // 2. Create the Stripe checkout session and hand off to Stripe.
      const stripeRes = await axios.post(`${API}/checkout/create`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (stripeRes.data.url) {
        window.location.href = stripeRes.data.url;
        return;
      }
      // No checkout URL means payment can't proceed — never claim the booking is
      // confirmed. It's saved as pending; tell the customer the truth.
      setError("We couldn't start secure checkout. Your booking is saved as pending in your dashboard — please try again, or contact support.");
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err?.response?.data?.detail || "Something went wrong and your payment didn't go through. Please try again — you haven't been charged.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center py-24"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;

  // Provider fetch failed / returned nothing, OR the service_id in the link no
  // longer matches a service on this provider (deleted/renamed service, stale
  // AI-plan card or "Book again" deep-link). Either way show a clean not-found
  // state instead of a bookable $0 form (which would charge $0 / crash on submit).
  if (!provider || !service) return (
    <div className="min-h-screen bg-background" data-testid="checkout-provider-missing">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-24 text-center space-y-5">
        <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto"><MapPin size={22} className="text-primary" /></div>
        <h1 className="text-2xl font-display font-semibold text-foreground">We couldn't load this provider</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">This provider or service may no longer be available. Browse other pros or plan your event with our AI.</p>
        <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
          <Link to="/browse" className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer no-underline inline-flex items-center justify-center">Browse providers</Link>
          <Link to="/ai-planner" className="border border-border bg-card text-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim hover:bg-secondary/60 hover:text-primary cursor-pointer no-underline inline-flex items-center justify-center gap-1.5"><Lightning size={15} weight="fill" /> Plan with AI</Link>
        </div>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-hero-wash" data-testid="booking-success">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-20 text-center space-y-5">
        <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto shadow-soft fade-up"><ShieldCheck size={32} /></div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground fade-up" style={{ animationDelay: '50ms' }}>Booking confirmed!</h1>
        <p className="text-sm text-muted-foreground leading-relaxed fade-up" style={{ animationDelay: '100ms' }}>Your payment is held securely until the service is complete. You'll receive a confirmation shortly. You can now message the provider in the Messages tab.</p>
        <button onClick={() => navigate('/dashboard')} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer fade-up" style={{ animationDelay: '150ms' }} data-testid="go-to-dashboard-button">Go to Dashboard</button>
      </div>
    </div>
  );

  const emoji = CAT_EMOJI[provider?.category] || '📋';
  const reviewCount = provider?.review_count || 0;
  const rating = provider?.rating_avg || 0;
  const name = provider?.business_name || '';


  const inputCls = "w-full h-11 rounded-xl border border-border bg-card px-4 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus:ring-2 focus:ring-primary/40";
  const labelCls = "block text-sm font-medium text-foreground mb-1.5";

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] cursor-pointer mb-4" data-testid="back-button">
          <ArrowLeft size={16} /> Back to browse
        </button>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground mb-1">You're almost there</h1>
        <p className="text-sm text-muted-foreground mb-8">Just a few details to lock in your booking — no charge until you confirm.</p>

        <div className="grid md:grid-cols-5 gap-8">
          <form onSubmit={handleSubmit} className="md:col-span-3 space-y-5">
            {/* Provider header with trust signals */}
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5" data-testid="provider-header">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center text-2xl shrink-0">{emoji}</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground" data-testid="checkout-provider-name">{name}</h3>
                  <p className="text-xs text-muted-foreground">{provider?.description?.slice(0, 50)} &middot; {provider?.city}</p>
                  <div className="flex items-center gap-3 mt-1.5">
                    <div className="flex items-center gap-1">
                      <Star size={12} weight="fill" className="text-amber-400" />
                      <span className="text-xs font-semibold text-foreground">{rating}</span>
                      <span className="text-[11px] text-muted-foreground">({reviewCount})</span>
                    </div>
                    {provider?.verified && (
                      <span className="flex items-center gap-1 text-[10px] font-medium text-primary bg-secondary px-2 py-0.5 rounded-full">
                        <CheckCircle size={10} weight="fill" /> Verified
                      </span>
                    )}
                  </div>
                </div>
              </div>

            </div>

            <div>
              <label className={labelCls}>Date</label>
              <input type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)} required className={inputCls} data-testid="booking-date-input" />
            </div>
            {!isEvent && (
              <div>
                <label className={labelCls}>Duration</label>
                <select value={duration} onChange={e => setDuration(Number(e.target.value))} className={`${inputCls} cursor-pointer`} data-testid="duration-select">
                  {DURATIONS.map(d => <option key={d.hours} value={d.hours}>{d.label}</option>)}
                </select>
              </div>
            )}
            {isPerPerson && (
              <div>
                <label className={labelCls}>Number of guests</label>
                <input type="number" min="1" max="10000" value={guests}
                  onChange={e => setGuests(Math.max(1, Number(e.target.value) || 1))}
                  required className={inputCls} data-testid="guests-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Priced per person — total updates with guest count.</p>
              </div>
            )}
            <div>
              <label className={labelCls}>Location / address</label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                placeholder="Start typing an address or place…"
                className={inputCls.replace('px-4', 'pr-4')}
                testId="address-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Start typing and pick the exact address from the list.</p>
            </div>
            <div>
              <label className={labelCls}>Special notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any special requirements..." className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus:ring-2 focus:ring-primary/40 resize-none" data-testid="notes-input" />
            </div>

            {/* Trust reassurance */}
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck size={16} weight="fill" className="text-primary shrink-0" />
              <span>Secure escrow payment — funds are only released once the job is done.</span>
            </div>

            {error && (
              <div className="bg-red-500/10 text-red-600 rounded-xl px-4 py-3 text-sm font-medium" data-testid="checkout-error">{error}</div>
            )}
            <button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground rounded-xl px-6 h-12 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="confirm-booking-button">
              {submitting ? 'Processing...' : 'Confirm & pay securely'}
            </button>

            <TrustBar />
          </form>

          {/* Price sidebar */}
          <div className="md:col-span-2 space-y-4">
            {/* Trust stats — real data only */}
            <div className="grid grid-cols-3 gap-2" data-testid="trust-stats">
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-3 text-center">
                <div className="text-lg font-display font-semibold text-foreground tabular-nums">{rating || '—'}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">Rating</div>
              </div>
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-3 text-center">
                <div className="text-lg font-display font-semibold text-foreground tabular-nums">{reviewCount}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">Reviews</div>
              </div>
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-3 text-center">
                <div className="text-lg font-display font-semibold text-primary flex items-center justify-center"><ShieldCheck size={18} weight="fill" /></div>
                <div className="text-[10px] text-muted-foreground mt-0.5">Escrow protected</div>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 sticky top-24" data-testid="order-summary">
              <div className="space-y-3 text-sm border-b border-border pb-4 mb-3 tabular-nums">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">{provider?.business_name} — {isEvent ? 'Event booking' : `${duration}h`}</span><span className="font-medium text-foreground">{money(total)}</span></div>
              </div>
              {(() => {
                const depPct = provider?.deposit_pct;
                // Whole-day UTC delta, matching the backend's _days_until — a
                // local-time/rounded calc disagreed by a day near the boundary,
                // so the page showed one "charged today" amount while Stripe
                // charged another.
                let daysOut = 0;
                if (bookingDate) {
                  const [y, m, d] = bookingDate.split('-').map(Number);
                  const evt = Date.UTC(y, m - 1, d);
                  const now = new Date();
                  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
                  daysOut = Math.floor((evt - today) / 86400000);
                }
                const split = (depPct === 25 || depPct === 50) && daysOut >= 8;
                if (!split) {
                  return <div className="flex justify-between items-baseline mb-2 tabular-nums"><span className="text-sm font-semibold text-foreground">Total charged</span><span className="text-xl font-display font-semibold text-foreground">{money(total)}</span></div>;
                }
                const dep = Math.round(total * depPct) / 100;
                const balDate = new Date(new Date(bookingDate).getTime() - 7 * 86400000).toISOString().slice(0, 10);
                return (
                  <div data-testid="deposit-breakdown">
                    <div className="flex justify-between items-baseline mb-1 tabular-nums"><span className="text-sm font-semibold text-foreground">Due today ({depPct}% deposit)</span><span className="text-xl font-display font-semibold text-foreground">{money(dep)}</span></div>
                    <div className="flex justify-between text-xs text-muted-foreground tabular-nums mb-2"><span>Balance due {balDate}</span><span>{money(total - dep)}</span></div>
                  </div>
                );
              })()}
              {provider?.cancellation_policy && (
                <p className="text-[11px] text-muted-foreground mt-3" data-testid="checkout-policy">
                  <span className="font-medium text-foreground">{(CANCELLATION_POLICIES[provider.cancellation_policy] || CANCELLATION_POLICIES.flexible).label} cancellation.</span> {describePolicy(provider)}
                </p>
              )}
              <Link to="/trust" className="flex items-center justify-center gap-1.5 text-[11px] text-primary font-medium mt-3 hover:underline">
                <ShieldCheck size={12} weight="fill" /> Covered by the Bookr Guarantee
              </Link>
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground mt-2">
                <Lock size={12} className="text-primary" />
                <span>Payment powered by Stripe</span>
              </div>
            </div>

            {/* Why book on Bookr */}
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5" data-testid="why-bookr">
              <h4 className="text-sm font-display font-semibold text-foreground mb-4">Why book on Bookr?</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: <Lock size={14} />, title: 'Secure payments', desc: 'Funds held until job complete' },
                  { icon: <ChatCircle size={14} />, title: 'In-app messaging', desc: 'No contact info before booking' },
                  { icon: <ShieldCheck size={14} />, title: 'Bookr protection', desc: 'Replacement or refund if cancelled' },
                  { icon: <Star size={14} weight="fill" />, title: 'Verified providers', desc: 'ID + portfolio reviewed' },
                  { icon: <ArrowsClockwise size={14} />, title: 'Easy rebooking', desc: 'One-click book again' },
                  { icon: <Scales size={14} />, title: 'Dispute resolution', desc: 'Reviewed within 24 hours' },
                ].map((w, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="text-primary mt-0.5 shrink-0">{w.icon}</div>
                    <div><p className="text-[11px] font-medium text-foreground">{w.title}</p><p className="text-[10px] text-muted-foreground">{w.desc}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
